import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Swords, Flag, Clock, ShieldAlert, AlertTriangle, Check, X,
  Flame, Crown, UserCircle2, MessageSquare, ChevronLeft, Calendar,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type MatchDetail,
  type MatchAvailabilityRow,
  type MatchTimelineRow,
} from '../lib/api';
import { cn } from '../lib/cn';
import { clanLabelFrom } from '../lib/displayName';
import { CardAgreementPanel } from '../components/CardAgreementPanel';
import { isMatchSideCaptain } from '../lib/matchCaptainAccess';
import { hasOrgManagerMembershipOfEntity } from '../lib/orgAccess';
import { useEffectiveMe, useViewAs } from '../lib/viewAs';

// ─────────────────────────────────────────────────────────────────────────────
// Match Card — the living view of a match from scheduled → confirmed → archived.
//
// NOT a public/anonymous route. A previous "anonymous share link" bypass was
// REVERTED: /card/:matchId now lives behind AuthGate, and the backend
// GET /match/:matchId enforces access — only a match participant (or a global
// admin/founder) gets the data; everyone else is 401/403. Authenticated
// participants additionally get controls (respond, dispute, admin override).
//
// Polls every 3s via react-query. All state writes are atomic single-record
// ops on the backend (availability row, timeline event, dispute record).
// ─────────────────────────────────────────────────────────────────────────────

function stripClanPrefix(s: string | undefined): string { return (s || '').replace(/^CLAN#/, ''); }
// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// SeedingTablePanel — never render a raw Discord snowflake.
function shortId(discordId: string): string { return `…${discordId.slice(-6)}`; }
function formatDateTime(ts?: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}
function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function PublicMatchCard() {
  const { matchId: rawMatchId } = useParams();
  const [qs] = useSearchParams();
  const compId = qs.get('compId') || '';
  const matchId = rawMatchId ? decodeURIComponent(rawMatchId) : '';

  // Current user — may be undefined for anonymous visitors. apiFetch surfaces
  // 401 as an error; we swallow it so the page still renders for public.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  // View-as-aware copy of `me` — EVERY gate on this page reads THIS, so a
  // founder previewing a lower role stops being offered writes against a real
  // match. Declared before the early returns to satisfy the Rules of Hooks.
  //
  // IDENTITY legs (discordId ↔ leader1Id/leader2Id, captain-of) go through it
  // too, and that is the point (corrected 2026-08-02 — the comment here used
  // to say identity "rides through unchanged", which was true of the engine
  // then and is false now). A preview simulates a GENERIC persona of a role,
  // not the founder relabelled, so useEffectiveMe hands back a synthetic
  // discordId and no scoped grants. A generic member does not lead this match,
  // so the preview must not offer the leader's writes — keeping the founder's
  // real id here would have been the same bleed-through the engine strips
  // everywhere else. See lib/viewAs.ts's VIEW_AS_SIM_DISCORD_ID.
  //
  // Identity no-op whenever no view-as is active — see lib/viewAs.ts.
  const effectiveMe = useEffectiveMe(me);
  const { viewAs } = useViewAs();

  const matchQuery = useQuery({
    queryKey: ['match', matchId, compId],
    queryFn: () => api.match(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 3000,
  });

  const availQuery = useQuery({
    queryKey: ['match-availability', matchId, compId],
    queryFn: () => api.matchAvailability(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 3000,
  });

  const timelineQuery = useQuery({
    queryKey: ['match-timeline', matchId, compId],
    queryFn: () => api.matchTimeline(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 3000,
  });

  // The card row itself (2026-07-23) — this page renders the SAME date-and-time
  // agreement panel MatchCardLive does, because this is the page participants
  // are actually sent to and the agreement is what is blocking their match.
  // Same queryKey the panel invalidates on success, so a signature here
  // refreshes here. Failure is silent: a viewer the card endpoint refuses just
  // does not get the panel.
  const cardQuery = useQuery({
    queryKey: ['matchcard', matchId, compId],
    queryFn: () => api.matchCardGet(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 4000,
    retry: false,
  });
  const { data: allComps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  useEffect(() => {
    const m = matchQuery.data;
    if (m) document.title = `${clanLabelFrom(m.clan1DisplayName, m.clan1)} vs ${clanLabelFrom(m.clan2DisplayName, m.clan2)} — GG Anvil`;
  }, [matchQuery.data]);

  if (!matchId || !compId) {
    return <CardShell><p className="py-16 text-center font-mono text-sm text-iron-500">Missing match or competition id.</p></CardShell>;
  }
  if (matchQuery.isLoading) {
    return <CardShell><p className="py-16 text-center font-mono text-sm text-iron-500">Loading match…</p></CardShell>;
  }
  if (matchQuery.isError || !matchQuery.data) {
    return <CardShell><p className="py-16 text-center font-mono text-sm text-red-400">Match not found.</p></CardShell>;
  }

  const match = matchQuery.data;
  const availability: MatchAvailabilityRow[] = Array.isArray(availQuery.data) ? availQuery.data : [];
  const timeline: MatchTimelineRow[] = Array.isArray(timelineQuery.data) ? timelineQuery.data : [];

  // Agreement viewer identity — derived from the CARD row (the raw match row
  // the server signs against), exactly as MatchCardLive derives it: leader of a
  // side, or the captain of the team THAT SIDE fielded. isOrganiser is the
  // admin/org-manager leg that unlocks "agree for <side>".
  const cardMatch = cardQuery.data?.match;
  const comp = (allComps ?? []).find(c => c.id === compId);
  const isPlatformAdmin = effectiveMe?.role === 'founder' || effectiveMe?.role === 'admin';
  // `viewerCaptainOf` is the REAL caller's captaincies, computed server-side —
  // and the server does not yet role-filter reads under a preview (the open
  // gap logged in docs/product/VIEW-AS-TOGGLE.md). So under a preview it is
  // the founder's own captaincies, which would put runsSideN back to true by
  // the captain leg the moment the discordId leg stopped doing it. Dropped
  // while previewing — FAIL-CLOSED, deliberately: a generic captain of clan C
  // team N is not automatically the captain who fielded THIS match's side, and
  // a preview may not claim authority over a specific real match row. The
  // clan-scoped preview still drives every clan surface off the simulated
  // captaincies/simulatedClanScope the engine synthesises.
  const viewerCaptainOf = viewAs ? undefined : cardQuery.data?.viewerCaptainOf;

  return (
    <CardShell>
      <TopBar />
      <Header match={match} />
      {/* Accept/decline a CAPTAIN-PROPOSED match (status AWAITING_AGREEMENT).
          The full card (MatchCardLive) has always had this banner; the
          participant view — the page the opponent captain is actually DM'd to —
          did not, so the portal offered no way to accept and Discord was the
          only door. Same surfacing gap as the date/time agreement (2026-07-23).
          Only the opponent captain (leader2) is offered the click; the server
          (assertMayRespondToMatch) is the real gate. */}
      {cardMatch?.status === 'AWAITING_AGREEMENT' && effectiveMe?.discordId === cardMatch.leader2Id && (
        <AcceptMatchBanner
          matchId={matchId}
          compId={compId}
          clan1Label={clanLabelFrom(match.clan1DisplayName, match.clan1)}
          clan2Label={clanLabelFrom(match.clan2DisplayName, match.clan2)}
          scheduledAt={typeof cardMatch.scheduledAt === 'number' ? cardMatch.scheduledAt : match.scheduledAt}
        />
      )}
      {/* The agreement sits directly under the header — above everything else
          on the page — because on an unbuilt card it is the ONLY thing standing
          between these people and playing. It hides itself once the card is
          built (see CardAgreementPanel's own cardStatus guard). */}
      {cardMatch && (
        <CardAgreementPanel
          matchId={matchId}
          compId={compId}
          match={cardMatch}
          clan1Label={clanLabelFrom(match.clan1DisplayName, match.clan1)}
          clan2Label={clanLabelFrom(match.clan2DisplayName, match.clan2)}
          scheduledAt={typeof cardMatch.scheduledAt === 'number' ? cardMatch.scheduledAt : match.scheduledAt}
          viewer={{
            discordId: effectiveMe?.discordId,
            runsSide1: effectiveMe?.discordId === cardMatch.leader1Id
              || isMatchSideCaptain(viewerCaptainOf, cardMatch.clan1, cardMatch.clan1TeamNo),
            runsSide2: effectiveMe?.discordId === cardMatch.leader2Id
              || isMatchSideCaptain(viewerCaptainOf, cardMatch.clan2, cardMatch.clan2TeamNo),
            isOrganiser: isPlatformAdmin || hasOrgManagerMembershipOfEntity(effectiveMe, comp),
          }}
        />
      )}
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_320px]">
        {/* clanId is the canonical (CLAN#-stripped) form — drives api.clan(),
            the ['clan', clanId] queryKey, and the /clan/ Link. clanLabel is
            display-only (clanLabelFrom-formatted; clan display-name-
            preservation fix, Lisa, 2026-07-13). The availability filter below
            MUST stay keyed on the canonical stripClanPrefix(match.clan1) form,
            matching what MatchAvailabilityRow.clanName is actually written
            as server-side. */}
        <ClanPanel
          clanId={stripClanPrefix(match.clan1)}
          clanLabel={clanLabelFrom(match.clan1DisplayName, match.clan1)}
          availability={availability.filter(a => a.clanName === stripClanPrefix(match.clan1))}
        />
        <ClanPanel
          clanId={stripClanPrefix(match.clan2)}
          clanLabel={clanLabelFrom(match.clan2DisplayName, match.clan2)}
          availability={availability.filter(a => a.clanName === stripClanPrefix(match.clan2))}
        />
        <Timeline events={timeline} />
      </div>
      <VetoPanel match={match} />
      <ScreenshotsStrip screenshots={match.screenshots ?? []} />
      <ActionRow match={match} me={effectiveMe ?? null} matchId={matchId} compId={compId} />
    </CardShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Accept-match banner — the participant-view twin of MatchCardLive's
// AgreementBanner (opponent-captain branch). Only rendered for the opponent
// captain on an AWAITING_AGREEMENT match; the server enforces the real gate.
// ─────────────────────────────────────────────────────────────────────────────

function AcceptMatchBanner({ matchId, compId, clan1Label, clan2Label, scheduledAt }: {
  matchId: string; compId: string; clan1Label: string; clan2Label: string; scheduledAt?: number;
}) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    qc.invalidateQueries({ queryKey: ['match', matchId, compId] });
  };
  const agree = useMutation({
    mutationFn: () => api.agreeMatch(matchId, compId),
    onSuccess: () => { setErr(null); refresh(); },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed to agree'),
  });
  const decline = useMutation({
    mutationFn: () => api.declineMatch(matchId, compId),
    onSuccess: () => { setErr(null); refresh(); },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed to decline'),
  });
  const pending = agree.isPending || decline.isPending;

  return (
    <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/30 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-extrabold uppercase tracking-wider text-amber-200">
            Agree to this match?
          </div>
          <p className="mt-1 font-mono text-xs text-amber-100/80">
            {clan1Label} vs {clan2Label}
            {' · '}{scheduledAt ? formatDateTime(scheduledAt) : 'No scheduled time'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => agree.mutate()}
            disabled={pending}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            <Check size={14} strokeWidth={3} /> {agree.isPending ? 'Agreeing…' : 'Agree'}
          </button>
          <button
            type="button"
            onClick={() => decline.mutate()}
            disabled={pending}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-600/60 bg-red-950/40 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-red-200 hover:bg-red-900/50 disabled:opacity-50"
          >
            <X size={14} strokeWidth={3} /> {decline.isPending ? 'Declining…' : 'Decline'}
          </button>
        </div>
      </div>
      {err && <p className="mt-2 font-mono text-xs text-red-300">{err}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout primitives
// ─────────────────────────────────────────────────────────────────────────────

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen text-white">
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-forge-950/20 via-transparent to-transparent" />
      <div className="relative mx-auto flex max-w-[1400px] flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
        {children}
      </div>
    </div>
  );
}

function TopBar() {
  return (
    <div className="flex items-center justify-between">
      <Link
        to="/"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-iron-500 transition-colors hover:text-forge-300"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={3} /> Command Center
      </Link>
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-forge-400">
        <Flame className="h-3 w-3" strokeWidth={3} /> Match Card
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header — teams, time, status
// ─────────────────────────────────────────────────────────────────────────────

function Header({ match }: { match: MatchDetail }) {
  const clan1 = clanLabelFrom(match.clan1DisplayName, match.clan1);
  const clan2 = clanLabelFrom(match.clan2DisplayName, match.clan2);
  const scheduled = match.scheduledAt;

  return (
    <div className="border border-forge-900/40 bg-iron-950 p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center border border-forge-700/50 bg-iron-950">
            <Swords className="h-6 w-6 text-forge-400" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
              {clan1} <span className="text-iron-500">vs</span> {clan2}
            </h1>
            <p className="mt-1 flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-iron-500">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" strokeWidth={3} /> {formatDateTime(scheduled)}
              </span>
              <StatusPill status={match.status} />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'CONFIRMED' ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' :
    status === 'DISPUTED'  ? 'border-red-500/50 bg-red-500/10 text-red-300' :
    status === 'IN_PROGRESS' ? 'border-forge-500/50 bg-forge-500/10 text-forge-300' :
    'border-forge-900/60 bg-iron-950 text-iron-400';
  return (
    <span className={cn('inline-flex items-center gap-1.5 border px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-widest', tone)}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Availability was DEMOTED here on 2026-07-23, after a live run in which both
// captains clicked "yes" repeatedly and nothing happened: every click landed on
// /availability, and the portal log showed zero card-agree calls. The readiness
// bar ("Warming up", 0/6 meters) and the "Are you available? YES / NO" control
// were the two loudest things on this page, and NEITHER of them gates anything —
// the card builds on the date-and-time agreement, not on RSVPs. Ghost seats can
// never answer an RSVP either, so the meters sat at 0/6 forever and read as a
// blocker. The only surviving trace of availability is a chip on players who
// actually answered (from the Discord RSVP DM), which is honest headcount.
//
// The under-quota DM at the availability deadline (matchLifecycle's
// handleAvailDeadline) is unaffected — it is advisory and server-side.
// ─────────────────────────────────────────────────────────────────────────────
// Clan panels — member tiles with yes/no/pending state
// ─────────────────────────────────────────────────────────────────────────────

function ClanPanel({
  clanId, clanLabel, availability,
}: {
  // Canonical (CLAN#-stripped) id — drives api.clan(), the ['clan', clanId]
  // queryKey, and the /clan/ Link. NEVER swap for clanLabel below.
  clanId: string;
  // Human-visible name only (clanLabelFrom-formatted) — display text.
  clanLabel: string;
  availability: MatchAvailabilityRow[];
}) {
  const { data: clan } = useQuery({
    queryKey: ['clan', clanId],
    queryFn: () => api.clan(clanId),
    enabled: !!clanId,
  });

  const members = clan?.members ?? [];
  const availByUser = useMemo(() => {
    const m = new Map<string, MatchAvailabilityRow>();
    availability.forEach(a => m.set(a.discordId, a));
    return m;
  }, [availability]);

  return (
    <div className="border border-forge-900/40 bg-iron-950 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <Link
          to={`/clan/${encodeURIComponent(clanId)}`}
          className="font-display text-base font-black uppercase tracking-wider text-white hover:text-forge-300"
        >
          {clanLabel}
        </Link>
      </div>

      <ul className="space-y-1">
        {members.length === 0 && (
          <li className="py-4 text-center font-mono text-[10px] uppercase tracking-widest text-iron-500">
            Roster empty
          </li>
        )}
        {members.map(mem => {
          const a = availByUser.get(mem.discordId);
          const r = a?.response ?? 'PENDING';
          const isLeader = mem.role === 'LEADER';
          return (
            <li key={mem.discordId} className="flex items-center gap-2 border border-forge-900/30 bg-iron-950 px-2.5 py-1.5">
              {isLeader ? (
                <Crown className="h-3.5 w-3.5 shrink-0 text-forge-400" strokeWidth={2.5} />
              ) : (
                <UserCircle2 className="h-3.5 w-3.5 shrink-0 text-iron-500" strokeWidth={2.5} />
              )}
              <span className="min-w-0 flex-1 truncate font-display text-xs font-bold uppercase tracking-wider text-white">
                {mem.displayName || mem.gamertag || shortId(mem.discordId)}
              </span>
              <AvailChip response={r} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// PENDING deliberately renders NOTHING (2026-07-23). A roster of "pending"
// chips on a page whose real blocker is the date-and-time agreement is a wall
// of false alarm — and ghost seats are pending forever by construction. Only a
// real answer shows.
function AvailChip({ response }: { response: 'YES' | 'NO' | 'PENDING' }) {
  if (response === 'YES') {
    return (
      <span className="inline-flex items-center gap-1 border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 font-display text-[9px] font-black uppercase tracking-widest text-emerald-300">
        <Check className="h-2.5 w-2.5" strokeWidth={3} /> Yes
      </span>
    );
  }
  if (response === 'NO') {
    return (
      <span className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 font-display text-[9px] font-black uppercase tracking-widest text-red-300">
        <X className="h-2.5 w-2.5" strokeWidth={3} /> No
      </span>
    );
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline
// ─────────────────────────────────────────────────────────────────────────────

const KIND_ICON: Record<string, typeof Flag> = {
  SCHEDULED:             Calendar,
  AVAILABILITY_YES:      Check,
  AVAILABILITY_NO:       X,
  AVAILABILITY_DEADLINE: Clock,
  VETO_STARTED:          Flag,
  VETO_LOCKED:           Flag,
  ROSTER_SET:            UserCircle2,
  MATCH_STARTED:         Swords,
  SCORE_SUBMITTED:       MessageSquare,
  CONFIRMED:             Check,
  DISPUTE_OPENED:        AlertTriangle,
  ADMIN_OVERRIDE:        ShieldAlert,
  ARCHIVED:              Check,
};

function kindLabel(kind: string | undefined): string {
  if (!kind) return 'event';
  return String(kind).toLowerCase().replace(/_/g, ' ');
}

function Timeline({ events }: { events: MatchTimelineRow[] }) {
  const sorted = [...events].sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
  return (
    <aside className="border border-forge-900/40 bg-iron-950 p-4 lg:row-span-2">
      <p className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.25em] text-forge-500/80">
        Timeline
      </p>
      {sorted.length === 0 ? (
        <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-iron-500">
          Nothing yet
        </p>
      ) : (
        <ul className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
          {sorted.map((e, i) => {
            const Icon = (e.kind && KIND_ICON[e.kind]) || Flag;
            const label = kindLabel(e.kind);
            return (
              <li key={`${e.ts ?? i}-${i}`} className="flex gap-2">
                <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forge-400" strokeWidth={2.5} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-[11px] font-bold uppercase tracking-wider text-white">
                    {e.detail || label}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-iron-500">
                    {e.ts ? `${timeAgo(e.ts)} ago` : '—'} · {label}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Veto panel — embeds existing VetoApi state via vetoId, link-out for now
// ─────────────────────────────────────────────────────────────────────────────

function VetoPanel({ match }: { match: MatchDetail }) {
  const vetoId = match.vetoId;
  return (
    <div className="border border-forge-900/40 bg-iron-950 p-4">
      <div className="flex items-center justify-between">
        <p className="font-display text-[10px] font-black uppercase tracking-[0.25em] text-forge-500/80">
          Map Veto
        </p>
        {vetoId ? (
          <Link
            to={`/veto/${vetoId}`}
            className="inline-flex items-center gap-1.5 border border-forge-500/50 bg-forge-600/20 px-3 py-1 font-display text-[10px] font-black uppercase tracking-widest text-forge-200 transition-colors hover:bg-forge-500/30"
          >
            <Flag className="h-3 w-3" strokeWidth={3} /> Open veto
          </Link>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-widest text-iron-500">
            Not started
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screenshots strip
// ─────────────────────────────────────────────────────────────────────────────

type Screenshot = { userId: string; url: string };

function ScreenshotsStrip({ screenshots }: { screenshots: Screenshot[] }) {
  if (screenshots.length === 0) return null;
  return (
    <div className="border border-forge-900/40 bg-iron-950 p-4">
      <p className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.25em] text-forge-500/80">
        Evidence ({screenshots.length})
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {screenshots.map((s, i) => (
          <a
            key={`${s.userId}-${i}`}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 overflow-hidden border border-forge-900/50 hover:border-forge-500/60"
          >
            <img src={s.url} alt={`Screenshot by ${s.userId}`} className="h-32 w-auto" />
          </a>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Action row — role-gated, max 5 actions, includes Dispute modal launcher
//
// `me` is the caller's VIEW-AS-ADJUSTED identity (useEffectiveMe), never the
// raw /me row — so a founder previewing a lower role loses the admin actions
// here exactly as that role would. The IDENTITY leg goes too (corrected
// 2026-08-02): discordId is simulated, so `isLeader` reads false under a
// preview even when the founder really leads this match. A generic member of
// the previewed role does not lead it, and the preview renders the generic
// persona — not the founder with a different label.
// ─────────────────────────────────────────────────────────────────────────────

function ActionRow({
  match, me, matchId, compId,
}: {
  match: MatchDetail;
  me: { discordId: string; role: string } | null;
  matchId: string;
  compId: string;
}) {
  const [disputeOpen, setDisputeOpen] = useState(false);

  if (!me) {
    return (
      <div className="border border-forge-900/40 bg-iron-950 p-4">
        <p className="text-center font-mono text-[10px] uppercase tracking-widest text-iron-500">
          Log in to respond, submit evidence, or open a dispute.
        </p>
      </div>
    );
  }

  const isLeader = me.discordId === match.leader1Id || me.discordId === match.leader2Id;
  const isAdmin = me.role === 'admin' || me.role === 'founder';

  return (
    <>
      <div className="flex flex-wrap gap-2 border border-forge-900/40 bg-iron-950 p-3">
        {(isLeader || isAdmin) && (
          <Link
            to={`/match/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`}
            className="ember inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
          >
            <Swords className="h-3.5 w-3.5" strokeWidth={2.5} /> Submit / confirm
          </Link>
        )}
        <button
          onClick={() => setDisputeOpen(true)}
          className="inline-flex items-center gap-2 border border-red-500/40 bg-red-500/10 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-red-300 transition-colors hover:bg-red-500/20"
        >
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} /> Dispute
        </button>
        {isAdmin && (
          <Link
            to={`/admin/alerts`}
            className="inline-flex items-center gap-2 border border-forge-900/60 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-iron-300 transition-colors hover:border-forge-500/60 hover:text-forge-200"
          >
            <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.5} /> Alerts
          </Link>
        )}
        <Link
          to="/"
          className="inline-flex items-center gap-2 border border-forge-900/60 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-iron-300 transition-colors hover:border-forge-500/60 hover:text-forge-200"
        >
          <Calendar className="h-3.5 w-3.5" strokeWidth={2.5} /> Command Center
        </Link>
      </div>
      {disputeOpen && (
        <DisputeModal
          matchId={matchId}
          compId={compId}
          onClose={() => setDisputeOpen(false)}
        />
      )}
    </>
  );
}

function DisputeModal({ matchId, compId, onClose }: { matchId: string; compId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: () => api.openDispute(matchId, compId, reason.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['match', matchId, compId] });
      qc.invalidateQueries({ queryKey: ['match-timeline', matchId, compId] });
      onClose();
    },
    onError: (e: Error) => setErr(parseApiError(e).message || 'Failed to open dispute'),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form
          onSubmit={e => { e.preventDefault(); setErr(null); if (reason.trim()) submit.mutate(); }}
          className="w-full max-w-md border border-forge-900/40 bg-iron-950 p-5 shadow-2xl"
        >
          <div className="mb-3 flex items-start justify-between">
            <h3 className="font-display text-lg font-black uppercase tracking-wider text-white">
              Open a dispute
            </h3>
            <button type="button" onClick={onClose} className="text-iron-500 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 font-mono text-xs text-iron-400">
            Tell admins what's wrong. This goes on the match card and alerts staff. Don't spam.
          </p>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="E.g. opponent roster swapped a player after veto"
            rows={4}
            className="w-full border border-forge-900/60 bg-iron-950 px-3 py-2 font-mono text-xs text-white placeholder:text-iron-500 focus:border-forge-500/60 focus:outline-none"
          />
          {err && <p className="mt-2 font-mono text-xs text-red-400">{err}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-forge-900/60 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-iron-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason.trim() || submit.isPending}
              className="flex-1 border border-red-500/60 bg-red-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-red-500/40 disabled:opacity-50"
            >
              {submit.isPending ? 'Sending…' : 'Open dispute'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
